import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Link,
} from "@mui/material";
import dayjs from "dayjs";
import {
  TABLE_PAGINATION_SX,
  TABLE_PAPER_SX,
  TABLE_ROWS_PER_PAGE_OPTIONS,
  TABLE_TOOLBAR_SX,
  ROW_NUMBER_BADGE_SX,
} from "../../../../components/ui";
import { LloCasesToFormItem } from "../../../../api";
import {
  COLUMN_COUNT,
  HEAD_CELLS,
  OVERDUE_ROW_BG,
  ROW_NUMBER_WIDTH_PCT,
  TABLE_BODY_CELL_SX,
  TABLE_HEADER_CELL_SX,
} from "./constants";

type LloCasesToFormTableProps = {
  loading: boolean;
  hasLoadedData: boolean;
  rows: LloCasesToFormItem[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  return dayjs(value).format("MM/DD/YYYY");
}

function clioMatterUrl(matterId: number) {
  return `https://app.clio.com/nc/#/matters/${matterId}`;
}

export function LloCasesToFormTable({
  loading,
  hasLoadedData,
  rows,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: LloCasesToFormTableProps) {
  return (
    <Paper variant="outlined" sx={{ ...TABLE_PAPER_SX, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={TABLE_TOOLBAR_SX}
      >
        <Typography variant="body2" color="text.secondary">
          {hasLoadedData
            ? `${totalCount} case${totalCount === 1 ? "" : "s"} due to be formed`
            : "Load cases to see LLO matters whose complaint was filed 4+ months ago."}
        </Typography>
      </Stack>

      <TableContainer sx={{ flex: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...TABLE_HEADER_CELL_SX,
                  width: `${ROW_NUMBER_WIDTH_PCT}%`,
                }}
              >
                #
              </TableCell>
              {HEAD_CELLS.map((cell) => (
                <TableCell
                  key={cell.id}
                  sx={{ ...TABLE_HEADER_CELL_SX, width: `${cell.widthPct}%` }}
                >
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && hasLoadedData && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No LLO cases are currently due to be formed.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row, index) => {
                const overdue = (row.daysPastFormBy ?? 0) > 0;
                return (
                  <TableRow
                    key={row.matterId}
                    hover
                    sx={overdue ? { bgcolor: OVERDUE_ROW_BG } : undefined}
                  >
                    <TableCell sx={TABLE_BODY_CELL_SX}>
                      <Box sx={ROW_NUMBER_BADGE_SX}>{page * rowsPerPage + index + 1}</Box>
                    </TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>
                      {row.matterId ? (
                        <Link
                          href={clioMatterUrl(row.matterId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
                        >
                          {row.caseName || "—"}
                        </Link>
                      ) : (
                        row.caseName || "—"
                      )}
                    </TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>{row.stage || "—"}</TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>{row.raa || "—"}</TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>{row.ap || "—"}</TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>
                      {formatDate(row.complaintFiledDate)}
                    </TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>{formatDate(row.formByDate)}</TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>
                      {row.daysPastFormBy ?? 0}
                    </TableCell>
                    <TableCell sx={TABLE_BODY_CELL_SX}>{row.lloExpiration || "—"}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, next) => onPageChange(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[...TABLE_ROWS_PER_PAGE_OPTIONS]}
        sx={TABLE_PAGINATION_SX}
      />
    </Paper>
  );
}
